import { useSubmitEnrollmentJointConfirmInformationData } from '@egw/api'
import { EnrollmentForm, ModalDrawer, type EnrollmentFormField, type FieldValues } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { type FC, useCallback, useMemo, useState, useEffect } from 'react'
import type { EditLastNameModalProps, LastNameFormData } from './edit-last-name-modal.types'

const log = new Logger('Enrollment - Joint - EditLastNameModal')

export const EditLastNameModal: FC<EditLastNameModalProps> = ({
  isOpen,
  onClose,
  currentValue,
  currentFirstName,
  onSuccess,
}) => {
  const { t } = useTranslation('features-enrollment')

  const { mutate, isLoading: isSubmitting } = useSubmitEnrollmentJointConfirmInformationData()
  const [submitError, setSubmitError] = useState<string | undefined>()

  // Local state to track the updated value to prevent brief revert
  const [localLastName, setLocalLastName] = useState<string>(currentValue ?? '')

  // Update local state when currentValue changes (e.g., when modal reopens)
  useEffect(() => {
    setLocalLastName(currentValue ?? '')
  }, [currentValue])

  // Define form fields
  const fields: EnrollmentFormField[][] = useMemo(
    () => [
      [
        {
          name: 'lastName',
          type: 'text',
          label: t('joint.confirm_information.edit_modals.last_name_label'),
          placeholder: t('joint.confirm_information.edit_modals.last_name_placeholder'),
          required: true,
          isPII: true,
          testId: 'edit-lastName-input',
          validationRules: {
            pattern: {
              value: /^[A-Za-z.' -]{2,30}$/,
              message: t('joint.confirm_information.edit_modals.last_name_invalid'),
            },
          },
        },
      ],
    ],
    [t],
  )

  // Handle form submission
  const handleSubmit = useCallback(
    async (formData: FieldValues): Promise<void> => {
      // Clear any previous errors
      setSubmitError(undefined)

      // Cast to our specific form data type
      const data = formData as LastNameFormData

      // Update local state immediately to prevent revert during submission
      setLocalLastName(data.lastName)

      try {
        log.info('Submitting last name update')

        const response = await mutate({
          activePerson: {
            name: {
              firstName: currentFirstName ?? '', // Keep existing first name
              lastName: data.lastName,
            },
          },
        })

        // Check for API errors
        if (response?.isError) {
          log.error('Error updating last name', response.errors)
          setSubmitError(t('common.generic_error'))

          // Revert local state on API error
          setLocalLastName(currentValue ?? '')
          return
        }

        log.info('Last name updated successfully')

        // Call success callback with the updated last name
        if (onSuccess) {
          await Promise.resolve(onSuccess(data.lastName))
        }

        // Close modal
        onClose()
      } catch (error) {
        log.error('Error submitting last name update', error)
        setSubmitError(t('common.generic_error'))

        // Revert local state on error
        setLocalLastName(currentValue ?? '')
      }
    },
    [mutate, currentFirstName, onSuccess, onClose, t, currentValue],
  )

  return (
    <ModalDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={t('joint.confirm_information.edit_modals.edit_last_name_title')}
      testId="edit-lastName-modal"
    >
      <EnrollmentForm
        fields={fields}
        defaultValues={{ lastName: localLastName }}
        onSubmit={handleSubmit}
        submitButtonText={t('common.save')}
        isSubmitting={isSubmitting}
        submitError={submitError}
        mode="onChange"
        testId="edit-lastName-form"
      />
    </ModalDrawer>
  )
}

export type { EditLastNameModalProps }
